import AppComponent from '../base/app-component.ts';
import { formatNumero } from '../lib/utils.ts';
import type { SerieAndamentoGruppo } from '../lib/api/gruppo.ts';
import type { ChartJsInstance } from '../types/chartjs-browser.ts';

const MESI = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
const COLORE_TOTALE = '#05760d';

function formatPeriodo(periodo: string): string {
  if (!/^\d{6}$/.test(periodo)) return periodo;
  const anno = periodo.slice(0, 4);
  const mese = Number(periodo.slice(4, 6)) - 1;
  return `${MESI[mese] ?? periodo.slice(4, 6)} ${anno}`;
}

interface PeriodoOption {
  value: string;
  label: string;
  selected: boolean;
}

export class AndamentoGruppo extends AppComponent {
  static override componentName = 'wc-andamento-gruppo';

  protected override get templateId() { return 'andamento-gruppo'; }

  private loading = true;
  private error: string | null = null;
  private data: SerieAndamentoGruppo | null = null;
  private chartInstance: ChartJsInstance | null = null;

  protected override onConnected(): void {
    void this.carica();
  }

  protected override onDisconnected(): void {
    this.destroyChart();
  }

  protected override buildRenderingData(): Record<string, unknown> {
    if (this.loading) return { loading: true };
    if (this.error) return { loading: false, error: this.error };
    if (!this.data || this.data.serieTotale.length === 0) {
      return { loading: false, hasData: false };
    }

    const d = this.data;
    const periodiDa: PeriodoOption[] = d.periodiDisponibili.map(p => ({
      value: p,
      label: formatPeriodo(p),
      selected: p === d.periodoInizio,
    }));
    const periodiA: PeriodoOption[] = d.periodiDisponibili.map(p => ({
      value: p,
      label: formatPeriodo(p),
      selected: p === d.periodoFine,
    }));

    const csvUrl = `/gruppo/csv?periodoInizio=${encodeURIComponent(d.periodoInizio)}&periodoFine=${encodeURIComponent(d.periodoFine)}`;

    return {
      loading: false,
      hasData: true,
      periodiDa,
      periodiA,
      nPeriodi: d.nPeriodi,
      periodoInizioLabel: formatPeriodo(d.periodoInizio),
      periodoFineLabel: formatPeriodo(d.periodoFine),
      totaleLatestLabel: formatNumero(d.totaleLatest),
      latestPeriodoLabel: d.latestPeriodo ? formatPeriodo(d.latestPeriodo) : '',
      ultimoCalcoloLabel: d.ultimoCalcolo
        ? new Date(d.ultimoCalcolo.replace(' ', 'T') + 'Z').toLocaleString('it-IT')
        : '—',
      nTerritori: d.territori.length,
      territoriLabel: d.territori.map(t => t.nome).join(', '),
      territoriSaltati: d.territoriSaltati,
      csvUrl,
    };
  }

  protected override afterRender(): void {
    if (this.data && this.data.serieTotale.length > 0) {
      const canvas = this.$('[data-chart-canvas]') as HTMLCanvasElement | null;
      if (canvas) this.mountChart(canvas, this.data);
    }
    this.delegate.on('click', '[data-action="refresh"]', () => void this.refresh());
    this.delegate.on('submit', 'form[data-action="filtra"]', (e: Event) => {
      e.preventDefault();
      const form = e.target as HTMLFormElement;
      const fd = new FormData(form);
      const from = (fd.get('periodoInizio') as string) || undefined;
      const to = (fd.get('periodoFine') as string) || undefined;
      void this.carica(from, to);
    });
  }

  private async carica(periodoInizio?: string, periodoFine?: string): Promise<void> {
    this.loading = true;
    this.error = null;
    this.destroyChart();
    this.render();

    const result = await this.api.gruppo.serie(periodoInizio, periodoFine);
    this.loading = false;
    if (!result.ok) {
      this.error = result.error;
      this.data = null;
    } else {
      this.data = result.data;
    }
    this.render();
  }

  private async refresh(): Promise<void> {
    this.loading = true;
    this.error = null;
    this.destroyChart();
    this.render();

    const from = this.data?.periodoInizio;
    const to = this.data?.periodoFine;
    const result = await this.api.gruppo.refresh(from, to);
    this.loading = false;
    if (!result.ok) {
      this.error = result.error;
    } else {
      this.data = result.data;
    }
    this.render();
  }

  private mountChart(canvas: HTMLCanvasElement, data: SerieAndamentoGruppo): void {
    const ChartCtor = window.Chart;
    if (!ChartCtor) {
      this.error = 'Chart.js non disponibile';
      this.render();
      return;
    }

    this.destroyChart();

    const labels = data.serieTotale.map(p => formatPeriodo(p.periodo));

    const datasets: unknown[] = [
      {
        type: 'bar',
        label: 'Totale gruppo',
        data: data.serieTotale.map(p => p.count),
        backgroundColor: `${COLORE_TOTALE}33`,
        borderColor: COLORE_TOTALE,
        borderWidth: 1,
        order: 2,
      },
      ...data.seriePerTerritorio.map(serie => ({
        type: 'line',
        label: serie.nome,
        data: serie.punti.map(p => p.count),
        borderColor: serie.color,
        backgroundColor: serie.color,
        borderWidth: 2,
        tension: 0.25,
        pointRadius: 3,
        pointHoverRadius: 5,
        fill: false,
        order: 1,
      })),
    ];

    this.chartInstance = new ChartCtor(canvas, {
      type: 'bar',
      data: { labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: {
            display: true,
            position: 'bottom',
            labels: { boxWidth: 12, padding: 12 },
          },
          tooltip: {
            callbacks: {
              label(ctx: { dataset: { label?: string }; parsed: { y: number } }) {
                const lbl = ctx.dataset.label ?? '';
                return `${lbl}: ${formatNumero(ctx.parsed.y)}`;
              },
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            suggestedMax: this.normalizeMax(data.maxCount),
            ticks: { callback(value: unknown) { return formatNumero(value); } },
          },
          x: { ticks: { maxRotation: 0, autoSkip: true } },
        },
      },
    });
  }

  private destroyChart(): void {
    if (this.chartInstance) {
      this.chartInstance.destroy();
      this.chartInstance = null;
    }
  }

  private normalizeMax(value: number): number {
    if (value <= 0) return 10;
    const magnitude = 10 ** Math.floor(Math.log10(value));
    return Math.ceil(value / magnitude) * magnitude;
  }
}
